import WebApp from '@twa-dev/sdk';
import { ChatsResponse, MessagesResponse, Chat, UserInfo, UserbotSession } from '../types';


const API_URL = '';

class ApiClient {
  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': WebApp.initData,
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
      }

      if (response.status === 401) {
        throw new Error('Ошибка авторизации. Перезапустите приложение.');
      }

      if (response.status === 500) {
        throw new Error('Ошибка сервера. Попробуйте позже.');
      }

      throw new Error(errorMessage);
    }
    return response.json();
  }

  async getChats(skip = 0, limit = 0): Promise<ChatsResponse> {
    try {
      const response = await fetch(
        `${API_URL}/api/chats?skip=${skip}&limit=${limit}`,
        {
          headers: this.getHeaders(),
        }
      );

      return this.handleResponse<ChatsResponse>(response);
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Не удалось подключиться к серверу. Проверьте интернет-соединение.');
      }
      throw error;
    }
  }

  async getMessages(
    chatId: string,
    skip = 0,
    limit = 100,
    deletedOnly = false
  ): Promise<MessagesResponse> {
    try {
      let url = `${API_URL}/api/chats/${chatId}/messages?skip=${skip}&limit=${limit}`;
      if (deletedOnly) {
        url += '&deletedOnly=true';
      }
      const response = await fetch(
        url,
        {
          headers: this.getHeaders(),
        }
      );

      return this.handleResponse<MessagesResponse>(response);
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Не удалось подключиться к серверу. Проверьте интернет-соединение.');
      }
      throw error;
    }
  }

  async getChat(chatId: string): Promise<Chat> {
    try {
      const response = await fetch(
        `${API_URL}/api/chats/${chatId}`,
        {
          headers: this.getHeaders(),
        }
      );

      return this.handleResponse<Chat>(response);
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Не удалось подключиться к серверу. Проверьте интернет-соединение.');
      }
      throw error;
    }
  }

  async trackVisit(path: string): Promise<{ success: boolean }> {
    try {
      const response = await fetch(`${API_URL}/api/visit`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ path }),
      });

      return this.handleResponse<{ success: boolean }>(response);
    } catch (error) {
      // Silently fail - visit tracking shouldn't break the app
      console.error('Failed to track visit:', error);
      return { success: false };
    }
  }

  async getUserInfo(): Promise<UserInfo> {
    try {
      const response = await fetch(`${API_URL}/api/user`, {
        headers: this.getHeaders(),
      });

      return this.handleResponse<UserInfo>(response);
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Не удалось подключиться к серверу. Проверьте интернет-соединение.');
      }
      throw error;
    }
  }


  async getUserbotSessions(): Promise<UserbotSession[]> {
    const response = await fetch(`${API_URL}/api/userbot/sessions`, { headers: this.getHeaders() });
    const data = await this.handleResponse<{ sessions: UserbotSession[] }>(response);
    return data.sessions;
  }

  async initUserbotSession(phone: string, apiId: number, apiHash: string): Promise<string> {
    const response = await fetch(`${API_URL}/api/userbot/sessions/init`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ phone, apiId, apiHash }),
    });
    const data = await this.handleResponse<{ sessionId: string }>(response);
    return data.sessionId;
  }

  async submitUserbotCode(sessionId: string, code: string): Promise<{ needPassword: boolean; invalidCode: boolean }> {
    const response = await fetch(`${API_URL}/api/userbot/sessions/${sessionId}/code`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ code }),
    });
    return this.handleResponse(response);
  }

  async submitUserbotPassword(sessionId: string, password: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/userbot/sessions/${sessionId}/password`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ password }),
    });
    await this.handleResponse(response);
  }

  async deleteUserbotSession(sessionId: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/userbot/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    await this.handleResponse(response);
  }

  getAuthHeaders(): HeadersInit {
    return this.getHeaders();
  }
}

export const apiClient = new ApiClient();